import React, { useState } from 'react';
import { number, string } from 'prop-types';
import moment from 'moment';
import { useFormik } from 'formik';

import clsx from 'clsx';

import { withRouter } from 'react-router';
import { connect } from 'react-redux';

import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';

import BobConfirmButton from '../BobConfirmButton';

import * as RuleActions from '../../actions/ruleActions';

import DateField from '../FormFields/DateField';
import validationSchema from './BobTimestampFilter.schema';

import classes from './BobTimestampFilter.module.css';

const RuleTimestampFilter = ({
    className,
    dateFormat,
    maxDate,
    minDate,
    ruleEndTimeStamp,
    ruleStartTimeStamp,
    setRuleStartTimestamp,
    setRuleEndTimestamp,
    showTimeSelect,
    timeFormat,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const handleToggle = () => {
        setMenuOpen(!menuOpen);
    };

    const handleClickAway = () => {
        setMenuOpen(false);
    };

    const { errors, touched, values, ...form } = useFormik({
        enableReinitialize: true,
        initialTouched:     {
            endTimestamp:   true,
            startTimestamp: true
        },
        initialValues: {
            endTimestamp:   ruleEndTimeStamp,
            startTimestamp: ruleStartTimeStamp
        },
        validateOnBlur: false,
        validationSchema
    });

    /**
     * Modifies the appropriate timestamp value based on the value of the
     * changed requisite date or time input.
     */
    function handleChange(name) {
        return (value, event) => {
            let parsedValue = null;
            if (value) {
                parsedValue = moment(value);
                // endtime should not be a future time. 
                if ( name === 'endTimestamp') {
                    const currentTimeStamp = moment().local();
                    if (parsedValue > currentTimeStamp) {
                        parsedValue = currentTimeStamp;
                    }
                }
                form.setFieldValue(name, parsedValue);
            }
            form.setFieldTouched(name, true);
            onFilterChange(name, parsedValue, value);
        };
    }

    const onFilterChange = async (filterName, value) => {
        if (filterName === 'startTimestamp') {
            if (value) {
                const start = value.local();
                form.setFieldValue('startTimestamp', start);
                
                const startTime = moment(start).seconds(0).milliseconds(0).toISOString();
                const endTime = moment(values.endTimestamp).seconds(0).milliseconds(0).toISOString();

                if (moment(startTime).isSameOrAfter(moment(endTime))) {
                    if (moment(start.local()).add(1, 'd').isSameOrAfter(moment(maxDate).local())) {
                        const newStart = moment(maxDate);
                        form.setFieldValue('endTimestamp', newStart.endOf('day'));
                    } else {
                        const newEnd = moment(start).add(1, 'd');
                        form.setFieldValue('endTimestamp', newEnd);
                    }
                }
                else {
                    const diff = moment(endTime).diff(moment(startTime), 'hours');
                    if (diff > 24) {
                        form.setFieldValue('endTimestamp', moment(startTime).add(24, 'h'));
                    }
                }
            }
        } else if (filterName === 'endTimestamp') {
            if (value) {
                const end = value.local();
                form.setFieldValue('endTimestamp', end);
                if (moment(end) <= moment(values.startTimestamp)) {
                    if (moment(end.local()).subtract(1, 'd').isBefore(moment(minDate).local())) {
                        const newEnd = moment(minDate);
                        form.setFieldValue('startTimestamp', newEnd.startOf('day'));
                    } else {
                        const newStart = moment(end).subtract(1, 'd');
                        form.setFieldValue('startTimestamp', newStart);
                    }
                }
                else {
                    const diff = moment(end).diff(moment(values.startTimestamp), 'hours');
                    if (diff > 24) {
                        form.setFieldValue('startTimestamp', moment(end).subtract(24, 'hours'));
                    }
                }
            }
        }
    };
    
    const handleGo = () => {
        setRuleStartTimestamp(values.startTimestamp);
        setRuleEndTimestamp(values.endTimestamp);
        handleToggle();
    };

    return (
        <ClickAwayListener
            mouseEvent={'onMouseUp'}
            onClickAway={() => handleClickAway()}
        >
            <div>
                <React.Fragment>
                    <React.Fragment>
                        <label className={classes.labelText}>
                            {`From ${moment(ruleStartTimeStamp).format('YYYY-MM-DD h:mm A')} To ${moment(ruleEndTimeStamp).format('YYYY-MM-DD h:mm A')}`}
                        </label>
                        <Button
                            onClick={handleToggle}
                            className={classes.datePickerButton}
                        >
                            <i className="fas fa-calendar-alt" />
                        </Button>
                    </React.Fragment>
                    { menuOpen ? 
                        <Paper className={ classes.datePickerContainer}>
                            <React.Fragment>
                                <div className={classes.bobTimestampFilter}>
                                    <div className={classes.startTime}>
                                        <div className={clsx(classes.labelContainer, className)}><label>{'Start '}</label></div>
                                        <DateField
                                            dateFormat={dateFormat}
                                            isClearable={false}
                                            margin="dense"
                                            maxDate={new Date(moment().local())}
                                            maxTime={new Date(moment().local().endOf('day'))}
                                            minTime={new Date(moment().local().startOf('day'))}
                                            minDate={minDate ? new Date(minDate) : ''}
                                            name="startTimestamp"
                                            onChange={handleChange('startTimestamp')}
                                            showTimeSelect={showTimeSelect}
                                            timeFormat={timeFormat}
                                            value={values.startTimestamp.toDate()}
                                            disabled={true}
                                            disableUnderline={true}
                                            InputProps={{
                                                popperoverride: classes.dateClassName
                                            }}
                                            open={true}
                                        />
                                    </div>
                                    <div className={classes.endTime}>
                                        <div className={clsx(classes.labelContainer, className)}><label>{'End '}</label></div>
                                        <DateField
                                            dateFormat={dateFormat}
                                            isClearable={false}
                                            margin="dense"
                                            maxTime={values.startTimestamp.clone().endOf('day').toDate()}
                                            minDate={minDate ? new Date(minDate) : moment().local().subtract(30, 'd').toDate()}
                                            minTime={values.startTimestamp.clone().startOf('day').toDate()}
                                            maxDate={maxDate ? new Date(maxDate) : ''}
                                            name="endTimestamp"
                                            onChange={handleChange('endTimestamp')}
                                            showTimeSelect={showTimeSelect}
                                            timeFormat={timeFormat}
                                            touched={touched.endTimestamp}
                                            value={values.endTimestamp.toDate()}
                                            disabled={true}
                                            disableUnderline={true}
                                            InputProps={{
                                                popperoverride: classes.dateClassName
                                            }}
                                            open={true}
                                        />
                                    </div>
                                </div>
                            </React.Fragment>
                            <div className={classes.ErrorContainer}>
                                {
                                    moment(values.endTimestamp).isSame(moment(values.startTimestamp)) ?
                                        <span>End timestamp must be after the start timestamp.</span>
                                        :
                                        null
                                }
                            </div>
                            <div className={classes.RuleTimestampFilterFooter}>
                                <div className={classes.ButtonContainer}>
                                    <BobConfirmButton
                                        label={'Go'}
                                        disabled={moment(values.endTimestamp).isSame(moment(values.startTimestamp)) ? true : false}
                                        onClick={handleGo}
                                    />
                                </div> 
                            </div>
                        </Paper>
                        : null
                    }
                </React.Fragment>
            </div>
        </ClickAwayListener>
    );
};

RuleTimestampFilter.defaultProps = {
    dateFormat:              'yyyy-MM-dd h:mm a',
    showTimeSelect:          true,
    timeFormat:              'h:mm a',
    timeIntervals:           15,
    disableIntervalSelector: false
};

RuleTimestampFilter.propTypes = {
    dateFormat:    string,
    timeFormat:    string,
    timeIntervals: number
};

const mapDispatchToProps = {
    ...RuleActions
};

const mapStateToProps = state => {
    return {
        ruleEndTimeStamp:   state.rules.ruleEndTimeStamp,
        ruleStartTimeStamp: state.rules.ruleStartTimeStamp,
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RuleTimestampFilter));
